// French copy, namespace `settings` (source language: English is typed against it):
// the settings page, the offers screen (targeting, monitoring), the
// subscription block and the API errors of those screens.

export const settings = {
  page: {
    title: "Paramètres",
    documentTitle: "Paramètres — Hune",
    sections: {
      subscription: "Abonnement",
      profile: "Profil",
      notifications: "Notifications",
      security: "Sécurité",
    },
    vatNote: "Prix HT, TVA calculée au paiement.",
  },
  /** Plan names, by plan id (the `plans` table keeps the French ones for the admin). */
  plans: {
    names: { free: "Gratuit", solo: "Solo", pro: "Pro" },
  },
  /** Monitoring cadence inside a sentence, by number of days ("Veille chaque semaine"). */
  cadence: {
    named: {
      1: "chaque jour",
      3: "deux fois par semaine",
      7: "chaque semaine",
      14: "toutes les deux semaines",
      30: "chaque mois",
    } as Record<number, string>,
    everyDays: (days: number) => `tous les ${days} jours`,
  },
  offers: {
    titleOne: "Mon offre",
    titleMany: "Mes offres",
    documentTitleOne: "Mon offre — Hune",
    documentTitleMany: "Mes offres — Hune",
    loadSubject: "vos offres",
    workspace: { before: "Offres de l’espace client ", after: " (lecture seule)." },
    empty:
      "Aucune offre surveillée pour l’instant. Ajoutez l’adresse de votre offre : l’agent en déduit votre cible, puis commence à chercher.",
    add: {
      title: "Ajouter une offre",
      limitReached: (plan: string, maxOffers: number) =>
        `Le plan ${plan} suit ${maxOffers > 1 ? `${maxOffers} offres` : `${maxOffers} offre`}, toutes prises : archivez-en une ou changez de plan.`,
      description: "L’agent lit la page, en déduit l’offre et la cible, puis lance sa veille.",
      urlLabel: "Adresse de la page qui présente l’offre",
      urlPlaceholder: "votre-autre-produit.fr",
      submit: "Ajouter l’offre",
      failed: "Impossible d’ajouter cette offre.",
    },
    archive: {
      button: "Archiver",
      confirm: (name: string) => `Archiver « ${name} » ? Sa veille s’arrête, ses leads restent.`,
      cancel: "Annuler",
    },
    card: {
      displayed: "Affichée",
      display: "Afficher",
      targeting: "Ciblage",
      edit: "Modifier",
      targetingPending: "Le ciblage apparaîtra après le premier passage.",
      watch: "Veille",
    },
    watchStatus: {
      active: (cadence: string) => `Veille ${cadence}`,
      paused: "Veille en pause",
    },
  },
  profileForm: {
    name: "Nom de l’offre",
    decisionMaker: "Décideur ciblé",
    summary: "Proposition de valeur",
    summaryPlaceholder: "Ce que l’offre promet, à qui, à la place de quoi.",
    segment: "Segment prioritaire (ICP)",
    segmentPlaceholder: "Le type d’organisation à viser en priorité.",
    event: "Événement rendant le besoin urgent",
    eventPlaceholder: "Ex. déménagement, recrutement, changement d’outil…",
    exclusions: "Profils à exclure",
    exclusionsPlaceholder: "Un profil par ligne.",
    criteria: {
      legend: "Critères de qualification",
      help: "Ce que l’enquête cherche sur chaque lead (taille, outils en place, équipe…). Les critères décisifs s’affichent en premier dans la fiche du lead.",
      label: "Critère",
      labelPlaceholder: "Ex. Outil de prise de RDV",
      goodSign: "Ce qui est un bon signe",
      goodSignPlaceholder: "Bon signe, ex. Doctolib ou téléphone seul",
      remove: (label: string) => `Retirer le critère ${label}`,
      searchTerms: "Mots à chercher",
      searchTermsPlaceholder: "Mots à chercher, séparés par des virgules",
      decisive: "Décisif",
      removeShort: "Retirer",
      add: "Ajouter un critère",
    },
    notes: "Consignes pour l’agent",
    notesPlaceholder: "Toute précision utile : zone géographique, taille d’équipe, secteurs à privilégier…",
    save: "Enregistrer",
    cancel: "Annuler",
    failed: "La mise à jour a échoué.",
    saved: "Profil enregistré : vos prochaines analyses partiront de cette version.",
  },
  profileView: {
    showLess: "Réduire",
    readMore: "Lire la suite",
    decisive: "décisif",
    rows: {
      summary: "Promesse",
      segment: "Cible",
      decisionMaker: "Décideur",
      triggers: "Déclencheurs",
      exclusions: "À exclure",
      criteria: "Critères",
      notes: "Consignes",
    },
  },
  watch: {
    /** Select options, by stored number of days. */
    cadenceOptions: {
      3: "Deux fois par semaine",
      7: "Chaque semaine · recommandé",
      14: "Toutes les deux semaines",
      30: "Chaque mois",
    },
    lockedSuffix: (plan: string) => ` · plan ${plan}`,
    switchLabel: (name: string) => `Veille automatique — ${name}`,
    switchText: "Veille automatique",
    cadence: "Cadence",
    saveFailed: "Impossible d’enregistrer la veille.",
    saved: "Veille enregistrée : l’agent repassera tout seul.",
    paused: "Veille en pause : plus aucun passage automatique.",
    freePlanNote: "Au plan Gratuit, l’agent repasse chaque mois.",
    freePlanUpgrade: "Une veille plus rapprochée",
    lastPass: (date: string) => `Dernier passage : ${date}.`,
    noPass: "Aucun passage encore.",
    nextPass: (date: string) => `Prochain passage le ${date}.`,
    notCovered: "Hors de votre plan : l’agent ne repasse pas sur cette offre.",
  },
  emailNotifications: {
    weeklyDigest: "Récapitulatif hebdomadaire par e-mail",
    weeklyDigestHelp: "Seulement quand l’agent a trouvé du nouveau, une fois par semaine au plus.",
    failedWithCause: (message: string) => `${message} Le réglage précédent est conservé.`,
    failed: "Impossible d’enregistrer : le réglage précédent est conservé.",
    loadFailed: "Impossible de lire vos préférences d’e-mail. Rechargez la page.",
  },
  billing: {
    loadSubject: "votre abonnement",
    /** Stripe subscription statuses. */
    statuses: {
      active: "Actif",
      trialing: "Essai",
      past_due: "Paiement en attente",
      unpaid: "Impayé",
      canceled: "Résilié",
      incomplete: "Paiement à finaliser",
    } as Record<string, string>,
    cadence: {
      monthly: "Mensuelle",
      weekly: "Hebdomadaire",
      twiceWeekly: "2 × / semaine",
    },
    adminAccount: "Compte administrateur",
    free: "Gratuit",
    pricePerYear: (price: string) => `${price} HT / an`,
    pricePerMonth: (price: string) => `${price} HT / mois`,
    yourPlan: "Votre plan",
    endsOn: (date: string) => `Se termine le ${date}`,
    renewsOn: (date: string) => `Renouvelé le ${date}`,
    trackedOffers: "Offres suivies",
    watch: "Veille",
    manualRuns: "Analyse à la demande",
    unlimited: "Illimitée",
    oneFree: "1 offerte",
    included: "Inclus",
    research: "Enquêtes et PDF",
    planColumn: "Plan",
    perMonthUnit: "HT / mois",
    currentPlan: "Plan actuel",
    choose: "Choisir",
    thanks: "Merci ! Votre abonnement est actif.",
    paymentReceived: "Paiement reçu : votre abonnement s’affichera dans un instant.",
    unavailable: "Le paiement est indisponible pour le moment.",
    manage: "Abonnement et factures",
    compare: "Comparer les plans",
    interval: "Échéance",
    monthly: "Mensuel",
    yearly: "Annuel · −20 %",
    comingSoon: "Paiement en ligne bientôt disponible : écrivez-nous pour changer de plan.",
    manageHint: "Changement de plan, carte, factures et résiliation : depuis « Abonnement et factures ».",
    seePlans: "Voir les plans",
  },
  /** Errors returned by the API routes of these screens. */
  errors: {
    signIn: "Connectez-vous.",
    signInToEditProfile: "Connectez-vous pour modifier votre profil d’offre.",
    offerUrlRequired: "Entrez l’adresse de l’offre à surveiller.",
    offerUrlInvalid: "Cette adresse n’est pas un site public.",
    addOfferFailed: "Impossible d’ajouter cette offre.",
    invalidOffer: "Offre invalide.",
    offerNotFound: "Offre introuvable.",
    saveOfferFailed: "Impossible d’enregistrer l’offre.",
    invalidBody: "Corps de requête invalide.",
    invalidField: (field: string) => `Valeur invalide pour « ${field} ».`,
    nothingToUpdate: "Aucun champ à mettre à jour.",
    profileNotFound: "Profil d’offre introuvable.",
    invalidPreference: "Préférence invalide.",
  },
};
